// Shared building blocks for the Check-ins UI (per the design handoff).
import React from 'react';
import {
  ClockCircleOutlined,
  RedoOutlined,
  ProfileOutlined,
  SmileOutlined,
} from '@ant-design/icons';

// The four ritual types (the only ones; no create-from-template).
export const TYPES = [
  {
    key: 'standup',
    name: 'Automated Standup',
    reco: true,
    engine: 'ai',
    standuptype: 'ai_standup',
    Icon: ClockCircleOutlined,
    tileBg: '#E7ECFB',
    iconColor: '#3358C4',
  },
  {
    key: 'retrospective',
    name: 'Retrospective',
    standuptype: 'retrospective',
    Icon: RedoOutlined,
    tileBg: '#FBF1DC',
    iconColor: '#C98A14',
  },
  {
    key: 'poker',
    name: 'Planning Poker',
    standuptype: 'planning_poker',
    Icon: ProfileOutlined,
    tileBg: '#EFE9FB',
    iconColor: '#6D4FCF',
  },
  {
    key: 'mood',
    name: 'Team Mood',
    standuptype: 'team_mood_standup',
    Icon: SmileOutlined,
    tileBg: '#E7F6EE',
    iconColor: '#1B9E57',
  },
];

export const typeByKey = (k) => TYPES.find((t) => t.key === k);

const AVATAR_PALETTE = [
  '#3D6FE0',
  '#1F9E63',
  '#B5612C',
  '#6D4FCF',
  '#C8413B',
  '#8A929C',
];

export function initials(name) {
  return (name || '?')
    .split(/[\s@.]+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function colorFor(seed) {
  const s = String(seed || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

export function IconTile({ type, large }) {
  const Icon = type.Icon;
  return (
    <div
      className={'ck21__tile' + (large ? ' ck21__tile--lg' : '')}
      style={{ background: type.tileBg }}
    >
      <Icon style={{ fontSize: large ? 24 : 19, color: type.iconColor }} />
    </div>
  );
}

export function Avatar({ name, seed }) {
  return (
    <div className="ck21__avatar" style={{ background: colorFor(seed || name) }}>
      {initials(name)}
    </div>
  );
}

const STATUS = {
  on_track: { bg: '#E7F6EE', color: '#1B7A45', label: 'On track' },
  at_risk: { bg: '#FBF1DC', color: '#9A6B07', label: 'At risk' },
  blocked: { bg: '#FBEAE9', color: '#C0392E', label: 'Blocked' },
};
export function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.on_track;
  return (
    <span className="ck21__pill" style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

const LEGACY = {
  active: { bg: '#E7F6EE', color: '#1B7A45', label: 'Active' },
  paused: { bg: '#FBF1DC', color: '#9A6B07', label: 'Paused' },
  disabled: { bg: '#EEF0F2', color: '#7A828D', label: 'Disabled' },
};
export function LegacyBadge({ state }) {
  const s = LEGACY[state] || LEGACY.disabled;
  return (
    <span className="ck21__pill" style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

export function AreaTag({ area }) {
  if (!area) return null;
  return <span className="ck21__tag">{area}</span>;
}

const DOT = {
  jira: '#2A6FE0',
  github: '#24292E',
  slack: '#4A154B',
  answered: '#1B9E57',
  faint: '#9AA2AC',
};
export function SourceChip({ kind, label }) {
  return (
    <span className="ck21__chip">
      <span className="dot" style={{ background: DOT[kind] || DOT.faint }} />
      {label}
    </span>
  );
}

export const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export function DayPills({ value, onChange }) {
  return (
    <div className="ck21__daypills">
      {DAYS.map((d) => {
        const on = value.includes(d);
        return (
          <div
            key={d}
            className={'ck21__day' + (on ? ' ck21__day--on' : '')}
            onClick={() =>
              onChange(on ? value.filter((x) => x !== d) : [...value, d])
            }
          >
            {d}
          </div>
        );
      })}
    </div>
  );
}

export function ReadFromChips({ value, onChange }) {
  const sources = ['Jira', 'GitHub', 'Slack'];
  return (
    <div className="ck21__readchips">
      {sources.map((s) => {
        const k = s.toLowerCase();
        const on = value.includes(k);
        return (
          <div
            key={s}
            className={'ck21__readchip' + (on ? ' ck21__readchip--on' : '')}
            onClick={() =>
              onChange(on ? value.filter((x) => x !== k) : [...value, k])
            }
          >
            <span className="dot" />
            {s}
          </div>
        );
      })}
    </div>
  );
}

// Source chips for one report entry, derived from its sources + draft.source.
export function entryChips(entry) {
  const chips = [];
  const s = entry.sources || {};
  const jira = s.jira || [];
  if (jira.length) {
    chips.push({
      kind: 'jira',
      label: 'Jira · ' + jira.map((i) => i.key).slice(0, 3).join(', '),
    });
  }
  const gh = s.github || {};
  const prs = [...(gh.prs_merged || []), ...(gh.prs_open || [])];
  if (prs.length) {
    chips.push({
      kind: 'github',
      label: 'GitHub · ' + prs.map((p) => 'PR #' + p.number).slice(0, 2).join(', '),
    });
  } else if ((gh.commits || []).length) {
    chips.push({ kind: 'faint', label: `GitHub · ${gh.commits.length} commits` });
  }
  if (entry.draft && entry.draft.source === 'asked_answered') {
    chips.push({ kind: 'answered', label: 'Slack · asked & answered' });
  } else if ((s.slack || []).length) {
    chips.push({ kind: 'slack', label: 'Slack · #channel' });
  }
  return chips;
}
